import { ENV } from '@/config';

// API base configuration
export const API_BASE_URL = ENV.apiBaseUrl;
export const API_VERSION = ENV.apiVersion;

// API endpoints
export const API_ENDPOINTS = {
  // Auth
  LOGIN: '/auth/login',
  REGISTER: '/auth/register',
  REFRESH: '/auth/refresh',
  LOGOUT: '/auth/logout',
  REQUEST_PASSWORD_RESET: '/auth/request-password-reset',
  RESET_PASSWORD: '/auth/reset-password',

  // Customers
  CUSTOMERS: '/customer/customers',
  CUSTOMER: (id: string): string => `/customer/customers/${id}`,

  // Companies
  COMPANIES: '/companies',
  COMPANY: (id: string): string => `/companies/${id}`,

  // Vehicle Groups (catalog categories)
  GROUPS: '/groups',
  GROUP: (id: string): string => `/groups/${id}`,
  GROUP_CONSTRAINTS: (id: string): string => `/groups/${id}/constraints`,
  GROUP_BASE_RATES: (id: string): string => `/groups/${id}/base-rates`,
  GROUP_STATION_FEES: (id: string): string => `/groups/${id}/station-fees`,
  GROUP_INSURANCE_FEES: (id: string): string => `/groups/${id}/insurance-fees`,
  GROUP_EXTRA_FEES: (id: string): string => `/groups/${id}/extra-fees`,
  GROUP_AGE_FEES: (id: string): string => `/groups/${id}/age-fees`,
  GROUP_KM_POLICIES: (id: string): string => `/groups/${id}/km-policies`,
  GROUP_EXTRA_DRIVER_FEES: (id: string): string => `/groups/${id}/extra-driver-fees`,
  GROUP_RATE_CALENDAR: (id: string): string => `/groups/${id}/rate-calendar`,
  GROUP_PRICING_COPY: (id: string): string => `/groups/${id}/pricing-copy`,

  // Vehicle Models (catalog items)
  MODELS: '/models',
  MODEL_BRANDS: '/models/brands',
  MODEL: (id: string): string => `/models/${id}`,
  MODEL_DUPLICATE: (id: string): string => `/models/${id}/duplicate`,
  MODEL_UPLOAD_REQUEST: (id: string): string => `/models/${id}/images/upload-request`,
  MODEL_PRIMARY_IMAGE: (id: string): string => `/models/${id}/images/primary`,
  MODEL_SECONDARY_IMAGES: (id: string): string => `/models/${id}/images/secondary`,
  MODEL_FROM_GLOBAL: (id: string): string => `/models/from-global/${id}`,

  // Global vehicle models: the tenant read API
  GLOBAL_VEHICLE_MODELS: '/global-vehicle-models',
  GLOBAL_VEHICLE_MODEL_BRANDS: '/global-vehicle-models/brands',
  GLOBAL_VEHICLE_MODEL: (id: string): string => `/global-vehicle-models/${id}`,

  // Vehicles (physical instances)
  VEHICLES: '/fleet/vehicles',
  VEHICLE: (id: string): string => `/fleet/vehicles/${id}`,
  VEHICLE_IMAGE_UPLOAD_REQUEST: (id: string): string =>
    `/fleet/vehicles/${id}/images/upload-request`,
  VEHICLE_PRIMARY_IMAGE: (id: string): string => `/fleet/vehicles/${id}/images/primary`,
  VEHICLE_SECONDARY_IMAGES: (id: string): string => `/fleet/vehicles/${id}/images/secondary`,
  VEHICLE_DOCUMENT_UPLOAD_REQUEST: (id: string): string =>
    `/fleet/vehicles/${id}/documents/upload-request`,
  VEHICLE_DOCUMENTS: (id: string): string => `/fleet/vehicles/${id}/documents`,
  VEHICLE_DOCUMENT: (id: string, documentId: string): string =>
    `/fleet/vehicles/${id}/documents/${documentId}`,
  VEHICLE_DUPLICATE: (id: string): string => `/fleet/vehicles/${id}/duplicate`,

  // Vehicle Damages
  DAMAGES: '/damages',
  DAMAGE: (id: string): string => `/damages/${id}`,
  VEHICLE_DAMAGES: (vehicleId: string): string => `/vehicles/${vehicleId}/damages`,
  DAMAGE_PHOTOS: (id: string): string => `/damages/${id}/photos`,
  DAMAGE_PHOTO: (id: string, photoId: string): string => `/damages/${id}/photos/${photoId}`,
  DAMAGE_PHOTOS_UPLOAD_REQUEST: '/damages/photos/upload-request',
  DAMAGE_PHOTOS_PROCESS: '/damages/photos/process',

  // Stations
  STATIONS: '/stations',
  STATION_AVAILABILITY: '/stations/availability',
  STATION: (id: string): string => `/stations/${id}`,
  STATION_FEES: (stationId: string): string => `/stations/${stationId}/fees`,

  // Reservations
  RESERVATIONS: '/reservations',
  RESERVATION: (id: string): string => `/reservations/${id}`,
  RESERVATION_STATUS: (id: string): string => `/reservations/${id}/status`,
  RESERVATION_PICKUP: (id: string): string => `/reservations/${id}/pickup`,
  RESERVATION_AGREEMENT: (id: string): string => `/reservations/${id}/agreement`,
  RESERVATION_UPGRADE: (id: string): string => `/reservations/${id}/upgrade`,
  RESERVATION_COMPLETE_DETAILS: (id: string): string => `/reservations/${id}/complete-details`,
  RESERVATION_COMPLETE_DETAILS_PREVIEW: (id: string): string =>
    `/reservations/${id}/complete-details/preview`,
  RESERVATION_PAYMENTS: (id: string): string => `/reservations/${id}/payments`,
  // Manager role (an Admin passes); newest first, paged with skip/take (take max 100).
  RESERVATION_AUDIT_LOG: (id: string): string => `/reservations/${id}/audit-log`,
  PARTY: (id: string): string => `/parties/${id}`,

  // Pricing
  PRICING_CALCULATE: '/pricing/calculate',

  // Tenant pricing settings
  SETTINGS: '/settings',
  SETTINGS_LOGO_UPLOAD_REQUEST: '/settings/logo/upload-request',
  SETTINGS_LOGO: '/settings/logo',

  // Rental profile (singleton per tenant; the client base URL adds /api/v1)
  RENTAL_PROFILE: '/rental-profile',

  // Discounts (admin, manager)
  DISCOUNT_CODES: '/discount-codes',
  DISCOUNT_CODE: (id: string): string => `/discount-codes/${id}`,
  DISCOUNT_RULES: '/discount-rules',
  DISCOUNT_RULE: (id: string): string => `/discount-rules/${id}`,

  // Extras (global add-ons like GPS, child seat)
  EXTRAS: '/extras',
  EXTRA: (id: string): string => `/extras/${id}`,

  // Insurances (global insurance options like Basic, Full, CDW)
  INSURANCES: '/insurances',
  INSURANCE: (id: string): string => `/insurances/${id}`,

  // Seasons (recurring yearly date periods for pricing tiers)
  SEASONS: '/seasons',
  SEASON: (id: string): string => `/seasons/${id}`,

  // Fees (unified fee system)
  FEES: '/fees',
  FEE: (id: string): string => `/fees/${id}`,

  // Fee cells: one group × season cell of a pricing grid
  FEE_CELLS: '/fee-cells',
  FEE_CELL_SEASONS: '/fee-cells/seasons',

  // Extra Fees (fee configuration per extra)
  EXTRA_FEES: (extraId: string): string => `/extras/${extraId}/fees`,

  // Insurance Fees (fee configuration per insurance)
  INSURANCE_FEES: (insuranceId: string): string => `/insurances/${insuranceId}/fees`,

  // Platform API (admin role only)
  ADMIN_RENTALS: '/admin/rentals',
  ADMIN_IMPERSONATE: '/admin/impersonate',
  ADMIN_END_IMPERSONATE: '/admin/end-impersonation',
  ADMIN_GLOBAL_VEHICLE_MODELS: '/admin/global-vehicle-models',
  ADMIN_GLOBAL_VEHICLE_MODEL: (id: string): string => `/admin/global-vehicle-models/${id}`,
  ADMIN_GLOBAL_MODEL_UPLOAD_REQUEST: (id: string): string =>
    `/admin/global-vehicle-models/${id}/images/upload-request`,
  ADMIN_GLOBAL_MODEL_PRIMARY_IMAGE: (id: string): string =>
    `/admin/global-vehicle-models/${id}/images/primary`,
  ADMIN_GLOBAL_MODEL_SECONDARY_IMAGES: (id: string): string =>
    `/admin/global-vehicle-models/${id}/images/secondary`,
  ADMIN_SECURITY_EVENTS: '/admin/security-events',
  ADMIN_USERS: '/admin/users',
  ADMIN_USER: (id: string): string => `/admin/users/${id}`,
  // Move or copy one record from the source rental (`rentalId`) to another rental
  ADMIN_RECORD_TRANSFER: (
    rentalId: string,
    entity: string,
    id: string,
    operation: 'move' | 'copy',
  ): string => `/admin/rentals/${rentalId}/${entity}/${id}/${operation}`,
  // What a move or copy of that record must map, and what refuses a move; writes nothing
  ADMIN_RECORD_TRANSFER_NEEDS: (rentalId: string, entity: string, id: string): string =>
    `/admin/rentals/${rentalId}/${entity}/${id}/transfer-needs`,

  // Users
  USER_PROFILE: '/users/me',
  USER_ONBOARDING: '/users/me/onboarding',
  USER_PASSWORD: '/users/me/password',
  USER_CHANGE_PASSWORD: '/users/me/change-password',

  // User management (Manager: own rental)
  USERS: '/users',
  USER: (id: string): string => `/users/${id}`,
  INVITATIONS: '/auth/invitations',
  INVITATION: (id: string): string => `/auth/invitations/${id}`,

  // Quick Start (trailing slash is part of the backend contract)
  QUICK_START: '/quick-start/',

  // Setup status (manager, or an admin in impersonation)
  SETUP_STATUS: '/setup/status',

  // Calendar
  CALENDAR: '/calendar',

  // Opening Hours (per-station weekly grid)
  STATION_OPENING_HOURS: (stationId: string): string => `/stations/${stationId}/opening-hours`,
  STATION_OPENING_HOURS_CALENDAR: (stationId: string): string =>
    `/stations/${stationId}/opening-hours/calendar`,
  STATION_PRICING_COPY: (stationId: string): string => `/stations/${stationId}/pricing-copy`,

  // Opening hours v2: out-of-hours fees shared by all stations, and special dates
  OUT_OF_HOURS_FEES: '/out-of-hours-fees',
  OUT_OF_HOURS_FEE: (feeId: string): string => `/out-of-hours-fees/${feeId}`,
  SPECIAL_DATES: '/special-dates',
  SPECIAL_DATE: (id: string): string => `/special-dates/${id}`,

  // Out-of-Hours Bands (per-station)
  STATION_OUT_OF_HOURS_BANDS: (stationId: string): string =>
    `/stations/${stationId}/out-of-hours-bands`,
  STATION_OUT_OF_HOURS_BAND: (stationId: string, bandId: string): string =>
    `/stations/${stationId}/out-of-hours-bands/${bandId}`,

  // Schedule Exceptions (holidays + closures)
  SCHEDULE_EXCEPTIONS: '/schedule-exceptions',
  SCHEDULE_EXCEPTION: (id: string): string => `/schedule-exceptions/${id}`,

  // Block Types
  BLOCK_TYPES: '/block-types',
  BLOCK_TYPE: (id: string): string => `/block-types/${id}`,

  // Vehicle Blocks
  VEHICLE_BLOCKS: '/vehicle-blocks',
  VEHICLE_BLOCK: (id: string): string => `/vehicle-blocks/${id}`,

  // Reservation Date Changes (Phase 2)
  RESERVATION_DATE_CHANGE_PREVIEW: (id: string): string =>
    `/reservations/${id}/date-change-preview`,
  RESERVATION_DATES: (id: string): string => `/reservations/${id}/dates`,

  // Deposits (Phase 3)
  RESERVATION_DEPOSITS: (reservationId: string): string =>
    `/reservations/${reservationId}/deposits`,
  DEPOSIT: (id: string): string => `/deposits/${id}`,

  // Dashboard
  DASHBOARD_PICKUPS: '/dashboard/pickups',
  DASHBOARD_DROPOFFS: '/dashboard/dropoffs',
  DASHBOARD_SERVICES: '/dashboard/services',
  DASHBOARD_FLEET_STATUS: '/dashboard/fleet-status',
  TASKS: '/tasks',
  TASK: (id: string): string => `/tasks/${id}`,
  DASHBOARD_PICKUPS_PDF: '/dashboard/pickups/pdf',
  DASHBOARD_DROPOFFS_PDF: '/dashboard/dropoffs/pdf',
  DASHBOARD_SERVICES_PDF: '/dashboard/services/pdf',
  DASHBOARD_TASKS_PDF: '/dashboard/tasks/pdf',

  // Health check
  HEALTH: '/health/hello',
} as const;

// HTTP methods
export const HTTP_METHODS = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
} as const;

// Request timeout
export const API_TIMEOUT = 30000; // 30 seconds

// Pagination defaults
export const DEFAULT_PAGE_SIZE = 10;
export const DEFAULT_CURRENT_PAGE = 1;
